import type { ChipTone } from '../interviews/Chip'

// One machine-proposed fix per shift, from public.v_shift_reconcile_proposal.
//
// Severity model (correction_type):
//   change_window  => 'minor'. Same car, same owner, same driver. Nothing
//                     financial moves; attribution only. The safe one.
//   change_vehicle => 'material'. The driver's pay is unchanged, but
//                     owner_statements is keyed on (owner_id, vehicle_id), so
//                     revenue and costs move between two owners' payouts. The
//                     risky one.
export type ReconcileProposal = {
  shift_id: string
  problem: 'vehicle_mismatch' | 'duplicate_slot' | string
  proposed_action: 'change_vehicle' | 'needs_investigation' | string
  target_field?: string | null
  current_value: string | null
  proposed_value: string | null
  evidence_grade: 'strong' | 'moderate' | 'weak' | 'pattern_only' | 'contradicted' | string
  rationale: string | null
  changes_earnings?: boolean | null
  changes_driver_earnings?: boolean | null
  changes_owner_earnings?: boolean | null
  correction_type: 'minor' | 'material' | 'unresolved' | string
  // The single strongest piece of evidence behind this proposal, from the new
  // evidence engine. Rendered as a small chip beside the evidence grade.
  strongest_signal?: string | null
  // Destination scoring from the evidence engine. displacement_score gauges how
  // sure the scorer is that the shift is misplaced; destination_score gauges how
  // sure it is about where the shift belongs; destination_signal names the
  // strongest cue behind the destination guess.
  displacement_score?: number | null
  destination_score?: number | null
  destination_signal?: string | null
  // Added columns: the machine now tells us whether the destination slot is
  // available and whether a correction for this shift has already been filed or
  // applied, so the reconcile button can reflect the true state.
  shift_date: string | null
  shift_window: string | null
  target_window_taken: boolean | null
  target_other_window_free: boolean | null
  already_requested: boolean | null
  already_applied: boolean | null
}

// One row from public.v_pending_corrections. The view flattens the changes
// jsonb into field / from_value / to_value and adds staleness so the review
// tab can render everything without re-querying.
export type PendingCorrection = {
  id: string
  shift_id: string
  correction_type: 'minor' | 'material' | string
  status: string
  field: string | null
  from_value: string | null
  to_value: string | null
  evidence_grade: string | null
  changes_owner_earnings: boolean | null
  reason: string | null
  proposed_at: string | null
  proposed_by: string | null
  proposed_by_name: string | null
  driver_name: string | null
  shift_date: string | null
  shift_window: string | null
  current_vehicle: string | null
  would_conflict: boolean | null
  is_stale: boolean | null
  stale_reason: string | null
}

// Evidence grade -> Chip tone. Green strong, amber moderate, grey weak /
// pattern_only, red contradicted.
export function evidenceTone(grade: string | null | undefined): ChipTone {
  switch (grade) {
    case 'strong':
      return 'green'
    case 'moderate':
      return 'amber'
    case 'contradicted':
      return 'red'
    case 'weak':
    case 'pattern_only':
    default:
      return 'neutral'
  }
}

export function evidenceLabel(grade: string | null | undefined): string {
  if (!grade) return 'n/a'
  return grade.replace(/_/g, ' ')
}

// The scorer names WHY it reached its conclusion. Third-party and physical
// evidence outranks habit inference, and the operator should be able to see
// which kind they are being asked to act on.
export function signalLabel(signal: string | null | undefined): string {
  switch (signal) {
    case 'partner_witness': return 'Partner witness'
    case 'vehicle_unavailable': return 'Vehicle unavailable'
    case 'gap_plus_confusable_plate': return 'Gap + look-alike plate'
    case 'gap_in_usual_vehicle': return 'Gap in usual car'
    case 'neighbouring_vehicle_habit': return 'Neighbouring-shift habit'
    case 'window_habit': return 'Window habit'
    case 'sandwich_pattern': return 'Sandwich pattern'
    case 'driver_submitted_form': return 'Driver submitted a form'
    default: return signal ? signal.replace(/_/g, ' ') : 'None'
  }
}

export function signalMeaning(signal: string | null | undefined): string {
  switch (signal) {
    case 'partner_witness': return 'The driver in the other window named someone else as their partner. This is the only third-party evidence we hold, and it outranks everything else.'
    case 'vehicle_unavailable': return 'This car was retired or had been dormant for months, so it almost certainly was not driven that day.'
    case 'gap_plus_confusable_plate': return 'This driver usual car has a hole on this date and the two plates differ by at most two characters.'
    case 'gap_in_usual_vehicle': return 'This driver usual car has a hole on this exact date.'
    case 'neighbouring_vehicle_habit': return 'Most of this driver shifts within three days used a different car. This is habit inference, which is weak.'
    case 'window_habit': return 'This driver normally works the other window. This is habit inference, which is weak.'
    case 'sandwich_pattern': return 'The shifts immediately before and after both used a different car.'
    case 'driver_submitted_form': return 'This driver submitted a form for this shift. Their own account outranks inference against them.'
    default: return 'No signal recorded.'
  }
}

// Shared window formatter (am_pm -> "AM-PM"). Kept here so the reconcile button
// tooltips can name the window without importing from the page.
export function windowLabel(w: string | null | undefined): string {
  if (!w) return 'n/a'
  return w
    .split('_')
    .map((p) => p.toUpperCase())
    .join('-')
}

// Whether the underlying proposal is contradicted or needs investigation. These
// can never be auto-proposed regardless of destination availability.
export function isActionable(p: ReconcileProposal): boolean {
  return p.proposed_action !== 'needs_investigation' && p.evidence_grade !== 'contradicted'
}

export function blockedReason(p: ReconcileProposal): string {
  if (p.proposed_action === 'needs_investigation') {
    return 'This shift has no actionable proposal, it needs human investigation.'
  }
  if (p.evidence_grade === 'contradicted') {
    return 'The evidence contradicts the proposed fix, so it cannot be auto-proposed.'
  }
  return ''
}

// The reconcile button's state, derived entirely from the proposal row. The
// order of precedence matters: an already-applied or already-requested shift is
// terminal, then the evidence checks, then destination availability.
export type ReconcileState =
  | { kind: 'applied' }
  | { kind: 'requested' }
  | { kind: 'blocked'; reason: string }
  | { kind: 'ready' }

export function reconcileState(p: ReconcileProposal): ReconcileState {
  if (p.already_applied === true) return { kind: 'applied' }
  if (p.already_requested === true) return { kind: 'requested' }
  if (!isActionable(p)) return { kind: 'blocked', reason: blockedReason(p) }
  if (p.target_window_taken === true) {
    const val = p.proposed_value || 'The target vehicle'
    const win = windowLabel(p.shift_window)
    const reason =
      p.target_other_window_free === true
        ? `${val} already has a driver in the ${win} window that day, but its other window is free. This may be a window error, not a vehicle error.`
        : `${val} is fully booked that day. This move is impossible.`
    return { kind: 'blocked', reason }
  }
  return { kind: 'ready' }
}

// Which filter bucket a reconcile proposal falls into. A shift with no proposal
// row at all counts as Blocked.
export type ProposalCategory = 'reconcile' | 'requested' | 'applied' | 'blocked'

export function proposalCategory(p: ReconcileProposal | undefined): ProposalCategory {
  if (!p) return 'blocked'
  const s = reconcileState(p)
  return s.kind === 'ready' ? 'reconcile' : s.kind
}
